/**
 * Practices, filed under one group per subject.
 *
 * Grouped by `subject.id` — the stable identity — never by `subject.name`,
 * which is locale text: grouping on it would split "Mathematics" from
 * "Matemáticas" into two groups the moment a request's language changed.
 * Groups are ordered by `subject.code`, the same column the catalog itself
 * sorts by (`listActiveSubjects`), so the order on screen does not depend on
 * which language wrote the subject's name. Practice order within a group is
 * exactly the input order — this function narrows nothing and reorders
 * nothing else, so a caller's own sort or filter still holds.
 */
export interface SubjectLike {
  id: string
  code: string
  name: string
}

export interface Subjected {
  subject: SubjectLike
}

export interface PracticeSubjectGroup<T> {
  subject: SubjectLike
  practices: readonly T[]
}

export function groupPracticesBySubject<T extends Subjected>(
  practices: readonly T[],
): readonly PracticeSubjectGroup<T>[] {
  const bySubjectId = new Map<string, T[]>()
  const subjects = new Map<string, SubjectLike>()

  for (const practice of practices) {
    const { subject } = practice
    subjects.set(subject.id, subject)
    const group = bySubjectId.get(subject.id)
    if (group) {
      group.push(practice)
    } else {
      bySubjectId.set(subject.id, [practice])
    }
  }

  return [...subjects.values()]
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((subject) => ({ subject, practices: bySubjectId.get(subject.id) ?? [] }))
}
